import { resolveTickSize } from '@/features/dashboard/lib/orderbook-data'
import { fetchChartData } from '@/service/chart.api'
import type { Exchange, Instrument } from '@/types/generated/common/types'
import { CandleFamily } from '@/types/generated/public_api'

const REFERENCE_LOOKBACK_SECONDS = 7 * 86400

/** The latest daily close: a price to pick the tick band with, before the chart has bars. */
async function fetchReferencePrice(exchange: Exchange, symbol: string): Promise<number | null> {
  const toSec = Math.floor(Date.now() / 1000)
  try {
    const data = await fetchChartData({
      symbol,
      exchange,
      timeframe: 86400,
      from: String(toSec - REFERENCE_LOOKBACK_SECONDS),
      to: String(toSec),
      limit: 1,
      families: [CandleFamily.CANDLE_FAMILY_CORE],
    })
    const close = data.candles?.close.at(-1)
    return close !== undefined && Number.isFinite(close) && close > 0 ? close : null
  } catch {
    return null
  }
}

/**
 * The price tick a chart should format with — the same rule as the order form and order book
 * (resolveTickSize). Venues with per-price-band ticks (Upbit KRW, Hyperliquid) publish the finest
 * band as `tickSize`, which on KRW-BTC near 115M meant 7 decimals; the band for the current
 * price is the real one. Only those venues pay the extra reference-price lookup.
 */
export async function resolveChartPriceTick(
  instrument: Instrument,
  exchange: Exchange
): Promise<number | null> {
  const referencePrice = instrument.tickSizeSteps?.length
    ? await fetchReferencePrice(exchange, instrument.symbol)
    : null
  return resolveTickSize(instrument, undefined, undefined, referencePrice)
}
